/**
 * useProAccessReady Hook
 *
 * Returns true once Pro-vs-Personal status is known with confidence.
 * Use this to gate UI that branches on `useHasProAccess()` and would otherwise
 * flash the Personal layout before the trial/subscription resolves.
 *
 * Not ready when:
 * - Auth store hasn't finished initializing yet,
 * - We're currently fetching the entitlement token from the server, or
 * - A signed-in user exists but their subscription hasn't been fetched yet
 *   (so paid-subscription Pro would otherwise read as Personal).
 *
 * Browser-mode Pro is decided synchronously, so it's always considered ready.
 * Signed-out users are ready immediately — there is no Pro to fetch.
 */

import { useAuthStore } from '../store/authStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { isBrowserModePro } from '../../../features/browser-mode/browserModeUtils';

export function useProAccessReady(): boolean {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const isResolvingEntitlement = useAuthStore((s) => s.isResolvingEntitlement);
  const hasUser = useAuthStore((s) => Boolean(s.user));
  const subscriptionHasLoadedOnce = useSubscriptionStore((s) => s.hasLoadedOnce);

  if (isBrowserModePro()) return true;
  if (!isInitialized) return false;
  if (isResolvingEntitlement) return false;
  if (hasUser && !subscriptionHasLoadedOnce) return false;
  return true;
}
